import { useEffect, useRef } from 'react';
import { BroadcastChannel } from 'broadcast-channel';

import type { GithubAuthConfig } from 'src/components/AuthNew/utils/socialNetworkUtils';

const createGithubAuthUrl = (config: GithubAuthConfig) => {
  const params = [
    `client_id=${config.client_id}`,
    `redirect_uri=${window.location.origin}/login/github`,
    'scope=user',
    'allow_signup=false',
  ].join('&');

  return ['https://github.com/login/oauth/authorize', params].join('?');
};

type GithubAuthFailPayload = {
  error?: string;
  error_description?: string;
  error_uri?: string;
  state?: string;
};

type GithubAuthSuccessPayload = {
  code?: string;
  state?: string;
};

export type GithubAuthPayload = GithubAuthFailPayload &
  GithubAuthSuccessPayload;

type GithubAuthResult = {
  success: boolean;
  payload: GithubAuthPayload;
};

type GithubAuthOnSuccess = (result: GithubAuthResult) => void;
type GithubAuthOnError = () => void;

/**
 * @desc процесс авторизации следующий:
 * 1. в новом окне открывается ссылка для авторизации на стороне Github
 * 2. создается Broadcast канал для обмена сообщениями между окном инициатором авторизации
 * и окном, где происходит запрос кода Github
 * 3. после подтверждения доступа, Github перенаправляет на redirect_url из ссылки и добавляет code или ошибки в параметры
 * 4. на странице шаблона (app/views/social_login/github.haml) скрипт считывает code или ошибки и отправляет их по Broadcast каналу
 * 5. хук передает полученные данные вызываемому методу.
 * */
export const useGithubAuth = () => {
  /** ссылка для авторизации в Github */
  /** полученный от Github код авторизации */
  const authCodeRef = useRef<GithubAuthResult | undefined>();
  const authChannel = useRef<BroadcastChannel>();

  const resetAuthCodeRef = (): void => {
    authCodeRef.current = undefined;
  };

  /** открыть окно для авторизации через Github */
  const openAuthPopup = (url: string): Window | null =>
    window.open(url, '_blank', 'popup');

  /** прослушивание закрытия окна авторизации Github */
  const createCloseAuthPopupListener =
    (onError: GithubAuthOnError) =>
    (instance: Window | null): void => {
      if (!instance) {
        return;
      }
      const intID = setInterval(() => {
        if (instance?.closed) {
          clearInterval(intID);
          if (!authCodeRef.current || !authCodeRef.current?.success) {
            onError();
          }
        }
      }, 500);
    };

  /** обработка ответа из результирующего окна авторизации через Github */
  const createAuthChannelResultHandler =
    (onSuccess: GithubAuthOnSuccess, onError: GithubAuthOnError) =>
    (result: GithubAuthResult) => {
      if (!result?.success) {
        // eslint-disable-next-line no-console
        console.error('GITHUB AUTH NOT SUCCESS: ', result);
        onError();
        return;
      }
      authCodeRef.current = result;
      onSuccess(result);
    };

  /**
   * Открыть канал для общения между окном инициатором авторизации
   * и окном в котором происходит получение кода от Github
   * */
  const openAuthChannel = (handler) => {
    const channel = new BroadcastChannel('auth-github-code');
    authChannel.current = channel;
    channel.addEventListener('message', handler);
  };

  const closeAuthChannel = () => {
    authChannel.current?.close();
  };

  const handleGithubLogin = (
    config: GithubAuthConfig,
    onSuccess: GithubAuthOnSuccess,
    onError: GithubAuthOnError,
  ): void => {
    try {
      closeAuthChannel();
      resetAuthCodeRef();

      const popup = openAuthPopup(createGithubAuthUrl(config));
      const listenCloseAuthPopup = createCloseAuthPopupListener(onError);
      listenCloseAuthPopup(popup);

      openAuthChannel(createAuthChannelResultHandler(onSuccess, onError));
    } catch (e) {
      console.error(e);
    }
  };

  /** закрытие канала обмена сообщениями */
  useEffect(() => closeAuthChannel, []);

  return {
    handleGithubLogin,
  };
};
